import React from "react";
import cn from "./cn";

/**
 * SegmentedControl — a row of mutually exclusive views, one pressed at a
 * time: "Open · Resolved · All", "Needs reply · Urgent · Unassigned".
 *
 * It is a group of toggle buttons (`aria-pressed`), not a tablist: pressing
 * one re-queries the content under it rather than revealing a panel that was
 * already there, which is what `Tabs` is for.
 *
 *   <SegmentedControl
 *     label="Show requests"
 *     value={view}
 *     onChange={setView}
 *     options={[{ value: 'open', label: 'Open', count: 3 }, …]}
 *   />
 *
 * Props:
 *   options  — [{ value, label, count? }]; `count` renders beside the label
 *              when it is a number
 *   value    — the pressed option's value. A value matching no option leaves
 *              none pressed, which is how a page says "a custom filter is on".
 *   onChange — (value) => void
 *   label    — REQUIRED. The group's accessible name.
 *   size     — "sm" (default) | "xs"
 */
const SEGMENT_SIZE_CLASSES = {
  sm: "tw-px-3 tw-py-1 tw-text-[0.8125rem]",
  xs: "tw-px-2 tw-py-0.5 tw-text-xs",
};

const SegmentedControl = ({ options = [], value, onChange, label, size = "sm", className = "" }) => (
  <div
    role="group"
    aria-label={label}
    className={cn("tw-inline-flex tw-max-w-full tw-flex-wrap tw-gap-0.5 tw-rounded-md tw-bg-surface-sunken tw-p-0.5", className)}
  >
    {options.map((option) => {
      const pressed = option.value === value;
      return (
        <button
          key={String(option.value)}
          type="button"
          aria-pressed={pressed}
          onClick={() => onChange?.(option.value)}
          className={cn(
            "tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-sm tw-border-0 tw-font-medium tw-transition-colors tw-focus-ring",
            SEGMENT_SIZE_CLASSES[size] || SEGMENT_SIZE_CLASSES.sm,
            pressed
              ? "tw-bg-surface-raised tw-text-content tw-shadow-card"
              : "tw-bg-transparent tw-text-content-muted hover:tw-text-content"
          )}
        >
          {option.label}
          {typeof option.count === "number" && (
            <span className={cn("tw-text-xs tw-tabular-nums", pressed ? "tw-text-primary" : "tw-text-content-subtle")}>
              {option.count}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

export default SegmentedControl;
